// Standalone notes — a plain notes app, deliberately NOTHING to do with cards.
//
// They used to hang off collected cards, which made a note about "book the
// stand for March" impossible unless it was pinned to a person. A note is just
// a note: it has text and a time, it lives only on this device, and it never
// travels — not in sync, not in a projection, not anywhere.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export interface Note {
  id: string;
  text: string;
  createdAt: number;
  updatedAt: number;
}

const DB_NAME = 'smartcard-notes';
const STORE = 'notes';

interface NotesDB extends DBSchema {
  notes: {
    key: string;
    value: Note;
    indexes: { 'by-updatedAt': number };
  };
}

let dbP: Promise<IDBPDatabase<NotesDB>> | null = null;

function db(): Promise<IDBPDatabase<NotesDB>> {
  if (!dbP) {
    dbP = openDB<NotesDB>(DB_NAME, 1, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by-updatedAt', 'updatedAt');
      },
    });
  }
  return dbP;
}

/** Every note, most recently touched first — the order a notes app reads in. */
export async function listNotes(): Promise<Note[]> {
  const database = await db();
  const all = await database.getAllFromIndex(STORE, 'by-updatedAt');
  return all.reverse();
}

/** Create a note, or rewrite an existing one's text. An empty text on an
 *  existing note deletes it — a blank saved note is a deletion someone made
 *  with the keyboard instead of the button. */
export async function saveNote(text: string, id?: string): Promise<Note | null> {
  const database = await db();
  const clean = text.trim();
  const now = Date.now();
  const existing = id ? await database.get(STORE, id) : undefined;
  if (!clean) {
    if (existing) await database.delete(STORE, existing.id);
    return null;
  }
  const note: Note = existing
    ? { ...existing, text: clean, updatedAt: now }
    : { id: newId(), text: clean, createdAt: now, updatedAt: now };
  await database.put(STORE, note);
  return note;
}

export async function deleteNote(id: string): Promise<void> {
  const database = await db();
  await database.delete(STORE, id);
}

/** randomUUID needs a secure context; the fallback keeps notes working on the
 *  odd http://LAN-IP dev view without pulling in an id library. */
function newId(): string {
  return crypto.randomUUID?.() ??
    `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
